// mdLite — render a SMALL, SAFE subset of Markdown to trusted HTML for chat bubbles.
//
// Safety model: the input is ESCAPED first (so any HTML/script in the model output
// becomes inert text), and only AFTER that do we insert a fixed whitelist of our own
// tags (<strong> <em> <code> <ul> <ol> <li> <br>). No attributes, no href/src, no
// raw pass-through — so the result is safe to bind with v-html even though the source
// text is untrusted LLM output.

function escapeHtml(s) {
  return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  }[c]));
}

// Inline marks, applied to already-escaped text.
function inline(s) {
  // 1. Pull inline code out first so its contents (e.g. get_fitting_table) are not
  //    reinterpreted as bold/italic. Placeholders use NUL, which cannot appear in
  //    escaped text.
  const codes = [];
  s = s.replace(/`([^`\n]+?)`/g, (_m, c) => {
    codes.push(c);
    return ' ' + (codes.length - 1) + ' ';
  });
  // 2. Flatten markdown links to just their text (never emit a clickable href).
  //    URL part tolerates one level of nested parens e.g. (javascript:alert(1)).
  s = s.replace(/\[([^\]\n]+?)\]\((?:[^()\n]|\([^()\n]*\))*\)/g, '$1');
  // 3. Bold before italic so the paired ** are consumed first.
  s = s
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/__(.+?)__/g, '<strong>$1</strong>')
    .replace(/(^|[^*])\*(?!\s)([^*\n]+?)\*(?!\*)/g, '$1<em>$2</em>')
    .replace(/(^|[^_])_(?!\s)([^_\n]+?)_(?!_)/g, '$1<em>$2</em>');
  // 4. Restore code spans.
  s = s.replace(/ (\d+) /g, (_m, i) => '<code>' + codes[+i] + '</code>');
  return s;
}

export function renderMarkdownLite(src) {
  // Strip trailing blank lines up front so trailing newlines in LLM output
  // (very common, e.g. after a list) don't produce a dangling <br> at the
  // end of the bubble.
  const lines = escapeHtml(src).replace(/\n+$/, '').split('\n');
  const out = [];
  let list = null; // 'ul' | 'ol' | null

  const closeList = () => { if (list) { out.push(`</${list}>`); list = null; } };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    // Note: the source is already HTML-escaped above, so a literal `'` never
    // becomes a bullet marker here — only -, *, and • start a list item.
    const bullet = line.match(/^\s*[-*•]\s+(.*)$/);
    const ordered = line.match(/^\s*(\d{1,3})[.)]\s+(.*)$/);

    if (bullet) {
      if (list !== 'ul') { closeList(); out.push('<ul>'); list = 'ul'; }
      out.push('<li>' + inline(bullet[1]) + '</li>');
    } else if (ordered) {
      if (list !== 'ol') { closeList(); out.push('<ol>'); list = 'ol'; }
      out.push('<li>' + inline(ordered[2]) + '</li>');
    } else if (line.trim() === '') {
      closeList();
      // blank line = paragraph break; avoid a leading gap
      if (out.length) out.push('<br>');
    } else {
      closeList();
      // headings (#..) render as an emphasized line, not giant <h1> in a small bubble
      const heading = line.match(/^\s*#{1,6}\s+(.*)$/);
      const content = heading ? '<strong>' + inline(heading[1]) + '</strong>' : inline(line);
      out.push(content + (i < lines.length - 1 && lines[i + 1].trim() !== '' && !/^\s*(?:[-*]|\d{1,3}[.)])\s+/.test(lines[i + 1]) ? '<br>' : ''));
    }
  }
  closeList();
  return out.join('');
}

export default renderMarkdownLite;
